import { Component, Input, OnInit } from '@angular/core';
import { Form, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { SpinnerButtonConfig } from '@/ui/spinner-button/spinner-button-config';
import {
  AnamneseService,
  AppFunctionService,
  AuthService,
  CareplanService,
  CaseService,
  CategoryService,
  ErrorService,
  FormgroupService,
  LanguageService,
  NnnRegistrationService,
  NotificationService,
  QuizResultsService,
} from '@/services';
import {
  AppFunction,
  CareplanRegistration,
  CareplanRegistrationRow,
  Category,
  FunctionSet,
  QuizResultRegistration,
  RegistrationState,
  Task,
  TaskWorkgroupFunctionRegistration,
  TaskWorkgroupRegistration,
  User,
  TaskWorkgroupState,
} from '@/models';
import { RegistrationDataModel } from '../types';
import { isCareplanNnnFunction } from '../../../utils/is-careplan-nnn-function';
import { TaskRegistrationDataComponent } from '../task-registration-data/task-registration-data.component';
import { ConfirmNoRegistrationsDialogComponent } from '../confirm-no-registrations-dialog/confirm-no-registrations-dialog.component';
import { TaskComponent } from '../task.component';
import { DialogDataModel } from '../types';

@Component({
  selector: 'app-task-student',
  templateUrl: './task-student.component.html',
  styleUrls: ['./task-student.component.scss'],
  standalone: false,
})
export class TaskStudentComponent implements OnInit {
  @Input() data: DialogDataModel;
  @Input() dialogRef: MatDialogRef<TaskComponent>;

  task: Task;

  availableFunctions: Category[] = [];
  selectedFunction: string;
  anamnesisFunctionSetFunctions: AppFunction[] = [];
  anamnesisSelectedForm: Form | null;

  newRegistrationFormGroup: UntypedFormGroup = new UntypedFormGroup({
    function: new UntypedFormControl(),
    anamnesisFunctionSets: new UntypedFormControl(),
    anamnesisForm: new UntypedFormControl(),
    anamnesisFormRegistration: new UntypedFormControl(),
    formGroupFormRegistration: new UntypedFormControl(),
    formRegistration: new UntypedFormControl(),
    careplanRegistration: new UntypedFormControl(),
    formGroupForm: new UntypedFormControl(),
  });

  formGroupFunctions: AppFunction[] = [];
  loadingFormGroup = false;

  selectedFunctionObject: AppFunction;
  functionRegistrations: RegistrationDataModel[] = [];

  commentsFormControl = new UntypedFormControl();

  quizResult: QuizResultRegistration;
  dataRegistration: unknown = null;

  resubmit = false;

  loadingFunctions = false;
  loadingAnamnesisFunctionSets = false;
  loadingFunctionRegistrations = false;
  loadingCareplanRegistrations = false;

  btnConfig: SpinnerButtonConfig = { text: '' };

  constructor(
    private authService: AuthService,
    private caseService: CaseService,
    private appFunctionService: AppFunctionService,
    private anamnesisService: AnamneseService,
    private notificationService: NotificationService,
    private quizResultsService: QuizResultsService,
    private categoryService: CategoryService,
    private errorService: ErrorService,
    private careplanService: CareplanService,
    private nnnRegistrationService: NnnRegistrationService,
    private formGroupService: FormgroupService,
    private languageService: LanguageService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.btnConfig.text = this.languageService.translateWithCap('general.actions.submit');

    this.data.registration.registrations.sort((a, b) => b.submittedTimestamp - a.submittedTimestamp);
    this.task = this.data.registration.task;

    if (this.task.isQuiz) {
      this.quizResultsService.getResultsByCaseId(this.data.caseId).subscribe({
        next: resp => {
          this.quizResult = resp as QuizResultRegistration;
        },
        error: err => this.errorService.onError(err),
      });
    }

    this.loadingFunctions = true;
    this.categoryService.getCategoriesForInstitutionId(this.data.careInstitutionId).subscribe({
      next: resp => {
        this.availableFunctions = [];
        const categories = resp as Category[];
        for (const cat of categories) {
          const catFuncs = [];
          for (const func of cat.categoryAppFunctions) {
            catFuncs.push(func);
            if (
              this.task.functionToSubmit &&
              this.task.functionToSubmit.id &&
              this.task.functionToSubmit.id === func.appFunction.id
            ) {
              this.task.functionToSubmit = func.appFunction;
              this.newRegistrationFormGroup.get('function')?.setValue(func.appFunction);
              this.onNewSubmissionFunctionSelectChange({ value: func.appFunction });
            }
          }
          if (catFuncs.length > 0) {
            cat.categoryAppFunctions = catFuncs;
            cat.categoryAppFunctions.sort((a, b) => a.order - b.order);
            this.availableFunctions.push(cat);
          }
        }
        this.loadingFunctions = false;
      },
      error: err => this.errorService.onError(err),
    });
  }

  newTaskWorkgroupRegistration: TaskWorkgroupRegistration;

  submit(): void {
    this.btnConfig.loading = true;
    if (
      this.task.toSubmit &&
      (!this.newTaskWorkgroupRegistration || this.newTaskWorkgroupRegistration.functionRegistrations.length < 1)
    ) {
      const dialogRef = this.dialog.open(ConfirmNoRegistrationsDialogComponent, { disableClose: true });
      dialogRef.afterClosed().subscribe(resp => {
        if (resp) {
          this.saveStudentSubmission();
        } else {
          this.btnConfig.loading = false;
        }
      });
    } else {
      this.saveStudentSubmission();
    }
  }

  private saveStudentSubmission(): void {
    const reg = new TaskWorkgroupRegistration();
    reg.done = true;
    if (this.commentsFormControl.value) {
      reg.comments = this.commentsFormControl.value;
    }
    if (this.newTaskWorkgroupRegistration) {
      reg.functionRegistrations = [];
      for (const taskReg of this.newTaskWorkgroupRegistration.functionRegistrations) {
        reg.functionRegistrations.push(taskReg);
      }
    }
    reg.submittedBy = new User();
    reg.submittedBy.id = this.authService.getLoggedInUserId();

    this.caseService
      .submitTask(this.data.caseId, this.data.registration.task.id, this.data.registration.workgroup.id, reg)
      .subscribe({
        next: () => {
          this.notificationService.success(
            this.languageService.translateWithCap('case.task.notifications.assignment-submitted')
          );
          this.btnConfig.loading = false;
          const tws = new TaskWorkgroupState();
          tws.name = this.task.toRate ? 'TO_RATE' : 'SUBMITTED';
          this.caseService
            .updateTaskWorkgroupState(
              this.data.caseId,
              this.data.registration.task.id,
              this.data.registration.workgroup.id,
              tws
            )
            .subscribe({
              next: () => {
                this.dialogRef.close(true);
                this.caseService.reloadCasesOnDashboard();
              },
              error: err => this.errorService.onError(err),
            });
        },
        error: err => this.errorService.onError(err),
      });
  }

  onNewSubmissionFunctionSelectChange(event: { value: AppFunction }): void {
    this.selectedFunction = event.value.type;
    this.dataRegistration = null;

    if (isCareplanNnnFunction(event.value)) {
      this.selectedFunction = 'careplan-nnn';
      this.selectedFunctionObject = event.value;
      this.loadingCareplanRegistrations = true;
      void this.loadCareplanNnnRegistrations();
    } else if (event.value.type === 'formGroup') {
      this.loadingFormGroup = true;
      this.formGroupFunctions = [];
      this.functionRegistrations = [];
      this.formGroupService.getFormsForFormGroup(event.value.id).subscribe({
        next: resp => {
          this.formGroupFunctions = resp as AppFunction[];
          this.loadingFormGroup = false;
          this.onFormGroupChange((resp as unknown as FunctionSet).forms);
          this.functionRegistrations = [];
        },
        error: err => this.errorService.onError(err),
      });
    } else if (event.value.type === 'anamnesis') {
      this.loadingAnamnesisFunctionSets = true;
      this.anamnesisService
        .getAnamnesisSetsForCareInstitutionId(this.data.careInstitutionId, true, this.data.patientId)
        .subscribe({
          next: resp => {
            this.anamnesisFunctionSetFunctions = [];
            this.loadingAnamnesisFunctionSets = false;
            this.onAnanmesisFunctionSetChange((resp as unknown as FunctionSet).forms);
            this.functionRegistrations = [];
          },
          error: err => this.errorService.onError(err),
        });
    } else if (event.value.type === 'careplan') {
      this.selectedFunctionObject = event.value;
      this.loadingCareplanRegistrations = true;
      this.careplanService.getCareplanRegistrations(this.data.patientId, RegistrationState.FINAL, true).subscribe({
        next: resp => {
          this.functionRegistrations = resp as RegistrationDataModel[];
          this.loadingCareplanRegistrations = false;
        },
        error: err => this.errorService.onError(err),
      });
    } else {
      this.loadingFunctionRegistrations = true;
      this.selectedFunctionObject = event.value;
      const endpoint = (event.value.backendEndpoint as string).replace('{id}', event.value.id);
      this.appFunctionService
        .getFunctionRegistrations(endpoint, this.data.patientId, this.authService.getLoggedInUserId())
        .subscribe({
          next: resp => {
            this.functionRegistrations = resp as RegistrationDataModel[];
            this.functionRegistrations.sort((a, b) => {
              if (a.finalSince > 1) return a.finalSince - b.finalSince;
              if (a.registered > 1) return a.registered - b.registered;
              return 0;
            });
            this.loadingFunctionRegistrations = false;
          },
          error: err => this.errorService.onError(err),
        });
    }
  }

  onFormGroupChange(event: unknown): void {
    if (event) {
      this.selectedFunctionObject = event as AppFunction;
      this.dataRegistration = null;
      this.checkSubmitButtonState();
      this.appFunctionService
        .getFunctionRegistrations(
          ((event as AppFunction).backendEndpoint as string).replace('{id}', (event as AppFunction).id),
          this.data.patientId,
          this.authService.getLoggedInUserId()
        )
        .subscribe({
          next: resp => {
            this.functionRegistrations = resp as RegistrationDataModel[];
          },
          error: err => this.errorService.onError(err),
        });
    }
  }

  onformGroupFunctionRegistrationChange(event: { value: RegistrationDataModel }): void {
    this.anamnesisSelectedForm = (event.value as unknown as { form: Form }).form;
    this.appFunctionService
      .getFunctionRegistrationData(
        (this.selectedFunctionObject.backendEndpoint as string).replace('{id}', this.selectedFunctionObject.id) +
          '/' +
          event.value.id
      )
      .subscribe({
        next: resp => {
          this.dataRegistration = resp;
          (this.dataRegistration as { anamnesis: boolean }).anamnesis = true;
        },
        error: err => this.errorService.onError(err),
      });
  }

  onAnanmesisFunctionSetChange(functions: AppFunction[]): void {
    this.anamnesisFunctionSetFunctions = functions;
    this.functionRegistrations = [];
    this.dataRegistration = null;
    this.checkSubmitButtonState();
  }

  onAnanmesisFunctionChange(event: { value: AppFunction }): void {
    this.selectedFunctionObject = event.value;
    this.dataRegistration = null;
    this.appFunctionService
      .getFunctionRegistrations(
        (event.value.backendEndpoint as string).replace('{id}', event.value.id),
        this.data.patientId,
        this.authService.getLoggedInUserId()
      )
      .subscribe({
        next: resp => {
          this.functionRegistrations = resp as RegistrationDataModel[];
        },
        error: err => this.errorService.onError(err),
      });
  }

  onAnanmesisFunctionRegistrationChange(event: { value: RegistrationDataModel }): void {
    this.loadingFunctionRegistrations = true;
    this.anamnesisSelectedForm = (event.value as unknown as { form: Form }).form;
    this.appFunctionService
      .getFunctionRegistrationData(
        (this.selectedFunctionObject.backendEndpoint as string).replace('{id}', this.selectedFunctionObject.id) +
          '/' +
          event.value.id
      )
      .subscribe({
        next: resp => {
          this.dataRegistration = resp;
          this.loadingFunctionRegistrations = false;
          (this.dataRegistration as { anamnesis: boolean }).anamnesis = true;
        },
        error: err => this.errorService.onError(err),
      });
  }

  onCareplanRegistrationChange(event: { value: RegistrationDataModel }): void {
    this.dataRegistration = event.value;
    (this.dataRegistration as { careplan: boolean }).careplan = true;
  }

  private async loadCareplanNnnRegistrations(): Promise<void> {
    const { data } = await this.nnnRegistrationService.getRegistrations(
      this.data.patientId,
      RegistrationState.FINAL,
      true
    );
    this.functionRegistrations = (data ?? []).map(registration => ({
      id: registration.id,
      finalSince: registration.finalSince,
      registered: registration.registeredOn,
      state: registration.state,
    }));
    this.loadingCareplanRegistrations = false;
  }

  onCareplanNnnRegistrationChange(event: { value: RegistrationDataModel }): void {
    this.dataRegistration = event.value;
  }

  onFormRegistrationChange(event: { value: RegistrationDataModel }): void {
    this.loadingFunctionRegistrations = true;
    this.appFunctionService
      .getFunctionRegistrationData(
        (this.selectedFunctionObject.backendEndpoint as string).replace('{id}', this.selectedFunctionObject.id) +
          '/' +
          event.value.id
      )
      .subscribe({
        next: resp => {
          this.dataRegistration = resp;
          this.checkSubmitButtonState();
          this.loadingFunctionRegistrations = false;
          (this.dataRegistration as { anamnesis: boolean }).anamnesis = false;
        },
        error: err => this.errorService.onError(err),
      });
  }

  addNewFunctionRegistration(): void {
    if (!this.newTaskWorkgroupRegistration) {
      this.newTaskWorkgroupRegistration = new TaskWorkgroupRegistration();
      this.newTaskWorkgroupRegistration.functionRegistrations = [];
    }
    const taskReg = new TaskWorkgroupFunctionRegistration();
    taskReg.function = this.selectedFunctionObject;
    taskReg.functionRegistrationId = (this.dataRegistration as { id: string }).id;
    taskReg.workgroupRegistration = new TaskWorkgroupRegistration();
    taskReg.workgroupRegistration.id = this.newTaskWorkgroupRegistration.id;
    taskReg.data = this.dataRegistration;
    if ((this.dataRegistration as { anamnesis: boolean }).anamnesis) {
      taskReg.data.anamnesisForm = this.anamnesisSelectedForm;
    }
    if (
      this.newTaskWorkgroupRegistration.functionRegistrations.findIndex(
        t => t.functionRegistrationId === taskReg.functionRegistrationId
      ) > -1
    ) {
      this.notificationService.warning(
        this.languageService.translateWithCap('case.task.notifications.registration-already-added')
      );
      return;
    }
    this.newTaskWorkgroupRegistration.functionRegistrations.push(taskReg);
    this.checkSubmitButtonState();
    const func = this.newRegistrationFormGroup.get('function')?.value;
    this.newRegistrationFormGroup.reset();
    this.newRegistrationFormGroup.get('function')?.setValue(func);
    setTimeout(() => {
      this.onNewSubmissionFunctionSelectChange({ value: func });
    }, 100);
    this.functionRegistrations = [];
    this.anamnesisFunctionSetFunctions = [];
    this.selectedFunction = '';
    this.dataRegistration = null;
    this.anamnesisSelectedForm = null;
  }

  deleteNewFunctionRegistrationFromList(reg: TaskWorkgroupFunctionRegistration): void {
    this.newTaskWorkgroupRegistration.functionRegistrations.splice(
      this.newTaskWorkgroupRegistration.functionRegistrations.findIndex(x => x === reg),
      1
    );
    this.checkSubmitButtonState();
  }

  openNewFunctionRegistration(reg: TaskWorkgroupFunctionRegistration): void {
    if (isCareplanNnnFunction(reg.function)) {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '65vw',
        width: '100%',
        maxHeight: '95vh',
        data: {
          careInstitutionId: this.data.careInstitutionId,
          patientId: this.data.patientId,
          dataRegistration: reg.data,
          form: reg.function,
          functionType: 'careplan-nnn',
        },
      });
    } else if (reg.function.type === 'careplan') {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '65vw',
        width: '100%',
        maxHeight: '95vh',
        data: {
          careInstitutionId: this.data.careInstitutionId,
          dataRegistration: reg.data,
          form: this.anamnesisSelectedForm,
          functionType: reg.function.type,
        },
      });
    } else if ((reg.data as { anamnesis: boolean }).anamnesis) {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '65vw',
        width: '100%',
        maxHeight: '95vh',
        data: {
          dataRegistration: reg.data,
          form: (reg.data as { anamnesisForm: Form }).anamnesisForm,
          functionType: 'form',
        },
      });
    } else {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '65vw',
        width: '100%',
        maxHeight: '95vh',
        data: {
          dataRegistration: reg.data,
          form: (reg.data as { form: Form }).form,
          functionType: 'form',
        },
      });
    }
  }

  openDataRegistration(type: string): void {
    if (type === 'careplan-nnn') {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '65vw',
        width: '100%',
        maxHeight: '95vh',
        data: {
          careInstitutionId: this.data.careInstitutionId,
          patientId: this.data.patientId,
          dataRegistration: this.dataRegistration,
          form: this.selectedFunctionObject,
          functionType: 'careplan-nnn',
        },
      });
    } else if (type === 'careplan') {
      this.careplanService
        .getCareplanRegistrationRows(
          this.data.patientId,
          RegistrationState.FINAL,
          (this.dataRegistration as { id: string }).id
        )
        .subscribe({
          next: resp => {
            const cp = this.dataRegistration as CareplanRegistration;
            cp.registrationRows = resp as CareplanRegistrationRow[];
            this.dialog.open(TaskRegistrationDataComponent, {
              maxWidth: '65vw',
              width: '100%',
              maxHeight: '95vh',
              data: {
                careInstitutionId: this.data.careInstitutionId,
                dataRegistration: cp,
                form: this.anamnesisSelectedForm,
                functionType: type,
              },
            });
          },
          error: err => this.errorService.onError(err),
        });
    } else {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '65vw',
        width: '100%',
        maxHeight: '95vh',
        data: {
          dataRegistration: this.dataRegistration,
          form: this.anamnesisSelectedForm,
          functionType: type,
        },
      });
    }
  }

  openFunctionRegistrationData(reg: TaskWorkgroupFunctionRegistration): void {
    if (isCareplanNnnFunction(reg.function)) {
      this.dialog.open(TaskRegistrationDataComponent, {
        maxWidth: '95vw',
        width: '100%',
        maxHeight: '75vh',
        data: {
          careInstitutionId: this.data.careInstitutionId,
          patientId: this.data.patientId,
          dataRegistration: { id: reg.functionRegistrationId },
          form: reg.function,
          functionType: 'careplan-nnn',
        },
      });
      return;
    }
    reg.loading = true;
    if (reg.function.type === 'careplan') {
      this.careplanService.getCareplanRegistrationById(reg.functionRegistrationId, this.data.patientId).subscribe({
        next: resp => {
          this.careplanService
            .getCareplanRegistrationRows(this.data.patientId, RegistrationState.FINAL, reg.functionRegistrationId)
            .subscribe({
              next: rowresp => {
                const cp = resp as CareplanRegistration;
                cp.registrationRows = rowresp as CareplanRegistrationRow[];
                this.dialog.open(TaskRegistrationDataComponent, {
                  maxWidth: '95vw',
                  width: '100%',
                  maxHeight: '75vh',
                  data: {
                    careInstitutionId: this.data.careInstitutionId,
                    dataRegistration: cp,
                    form: reg.function,
                    functionType: 'careplan',
                  },
                });
                reg.loading = false;
              },
              error: err => this.errorService.onError(err),
            });
        },
        error: err => this.errorService.onError(err),
      });
      return;
    }
    this.appFunctionService
      .getFunctionRegistrationData(
        reg.function.backendEndpoint.replace('{id}', reg.function.id) + '/' + reg.functionRegistrationId
      )
      .subscribe({
        next: resp => {
          reg.loading = false;
          this.dialog.open(TaskRegistrationDataComponent, {
            maxWidth: '65vw',
            width: '100%',
            maxHeight: '75vh',
            data: { dataRegistration: resp, form: reg.function, functionType: 'form' },
          });
        },
        error: err => this.errorService.onError(err),
      });
  }

  checkIfQuizResultPassed(): boolean {
    const passingScore = +this.quizResult.passingScore;
    const score = +this.quizResult.score;
    return score >= passingScore;
  }

  checkSubmitButtonState(): void {
    this.btnConfig.disabled = !(
      (this.newTaskWorkgroupRegistration &&
        this.newTaskWorkgroupRegistration.functionRegistrations &&
        this.newTaskWorkgroupRegistration.functionRegistrations.length > 0) ||
      this.commentsFormControl.value
    );
  }
}
